import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CreditCard, Calendar, User, CheckCircle, Loader2, Lock } from 'lucide-react';
import BootcampPaymentModal from '../components/bootcamp/BootcampPaymentModal';
import api from '../lib/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const BootcampPayPage = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    fetchBookingInfo();
  }, [bookingId]);

  const fetchBookingInfo = async () => {
    try {
      const res = await api.get(`/bootcamps/bookings/${bookingId}/payment-info`);
      if (res.data.already_paid) {
        setPaid(true);
      } else {
        setBooking(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Booking not found');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setPaid(true);
    setShowPayment(false);
    toast.success('Payment completed!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF007F]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <Card className="bg-[#111] border-[#333] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <p className="text-red-400 text-lg">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paid) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <Card className="bg-[#111] border-emerald-500/30 max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto" />
            <h1 className="text-2xl font-bold text-white">Payment Complete</h1>
            <p className="text-white/60">Thank you! Your place is confirmed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <Card className="bg-[#111] border-[#333] max-w-lg w-full">
        <CardContent className="p-6 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold text-white">{booking.bootcamp_name}</h1>
            <Badge className="bg-[#FF007F]/20 text-[#FF007F] border-[#FF007F]/30">
              Payment Required
            </Badge>
          </div>

          {/* Sibling group view (2+ siblings) */}
          {booking.is_sibling_group && Array.isArray(booking.siblings) ? (
            <div className="space-y-3 bg-white/5 rounded-lg p-4" data-testid="sibling-group-summary">
              <div className="text-xs uppercase tracking-wide text-white/50 mb-2">
                {booking.siblings.length} students · {booking.parent_name}
              </div>
              {booking.siblings.map((s) => (
                <div key={s.id} className="flex items-start justify-between gap-3 py-1.5 border-b border-white/5 last:border-0" data-testid={`sibling-row-${s.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <User className="w-4 h-4 text-[#FF007F] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-white text-sm truncate">{s.student_name}</div>
                      {s.sibling_discount_applied && (
                        <div className="text-[10px] text-pink-300 mt-0.5">30% sibling discount (−£{s.sibling_discount_amount.toFixed(2)})</div>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {s.sibling_discount_applied && (s.amount_before_sibling ?? s.original_amount) > s.amount && (
                      <div className="text-xs text-white/30 line-through">£{(s.amount_before_sibling ?? s.original_amount).toFixed(2)}</div>
                    )}
                    <div className="text-white font-semibold">£{s.amount.toFixed(2)}</div>
                  </div>
                </div>
              ))}
              {booking.start_date && (
                <div className="flex items-center gap-3 text-white/70 pt-2">
                  <Calendar className="w-4 h-4 text-purple-400" />
                  <span className="text-sm">
                    {new Date(booking.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
                    {booking.end_date && ` - ${new Date(booking.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between pt-3 border-t border-white/10">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-emerald-400" />
                  <span className="text-white/70 text-sm">Total to pay</span>
                </div>
                <div className="text-right">
                  {booking.combined_savings > 0 && (
                    <div className="text-[11px] text-pink-300">You save £{booking.combined_savings.toFixed(2)}</div>
                  )}
                  <div className="text-2xl font-bold text-white" data-testid="combined-total">
                    £{Number(booking.combined_total).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Single-student view (unchanged) */
            <div className="space-y-3 bg-white/5 rounded-lg p-4">
              <div className="flex items-center gap-3 text-white/70">
                <User className="w-4 h-4 text-[#FF007F]" />
                <span>{booking.student_name}</span>
              </div>
              {booking.start_date && (
                <div className="flex items-center gap-3 text-white/70">
                  <Calendar className="w-4 h-4 text-purple-400" />
                  <span>
                    {new Date(booking.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
                    {booking.end_date && ` - ${new Date(booking.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <CreditCard className="w-4 h-4 text-emerald-400" />
                <span className="text-2xl font-bold text-white">£{Number(booking.amount).toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Pay Button */}
          <Button
            onClick={() => setShowPayment(true)}
            className="w-full h-14 bg-[#FF007F] hover:bg-[#D6006A] text-white text-lg"
            data-testid="proceed-to-payment"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Proceed to Payment
          </Button>

          <p className="text-xs text-white/40 text-center flex items-center justify-center gap-1">
            <Lock className="w-3 h-3" />
            Secure payment powered by Stripe
          </p>
        </CardContent>
      </Card>

      {/* Payment Modal — pass `bookings` array for sibling groups so Stripe charges once for the whole family. */}
      {showPayment && (
        <BootcampPaymentModal
          open={showPayment}
          onClose={() => setShowPayment(false)}
          booking={booking.is_sibling_group ? undefined : {
            id: booking.booking_id,
            student_name: booking.student_name,
            amount: booking.amount,
            parent_email: booking.parent_email
          }}
          bookings={booking.is_sibling_group ? booking.siblings.map((s) => ({
            id: s.id,
            student_name: s.student_name,
            amount: s.amount,
            parent_email: booking.parent_email,
          })) : undefined}
          bootcamp={{
            name: booking.bootcamp_name,
            start_date: booking.start_date,
            end_date: booking.end_date,
            price: booking.is_sibling_group ? booking.combined_total : booking.amount
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default BootcampPayPage;
